import {
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { TOURNAMENT_ID } from '../config/app';

export interface AdminStatus {
  isAdmin: boolean;
  isGlobalAdmin: boolean;
  isTournamentAdmin: boolean;
}

/** Sign in anonymously (silent, no UI). Call once on app load. */
export async function ensureAnonymousAuth(): Promise<User> {
  if (auth.currentUser) return auth.currentUser;
  const cred = await signInAnonymously(auth);
  return cred.user;
}

/** Admin email/password sign-in */
export async function adminSignIn(email: string, password: string): Promise<User> {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

/** Check if the current user has admin privileges (Firestore document exists) */
export async function checkIsAdmin(uid: string): Promise<AdminStatus> {
  const [globalAdminSnap, tournamentAdminSnap] = await Promise.all([
    getDoc(doc(db, 'admins', uid)),
    getDoc(doc(db, 'tournaments', TOURNAMENT_ID, 'admins', uid)),
  ]);
  const isGlobalAdmin = globalAdminSnap.exists();
  const isTournamentAdmin = tournamentAdminSnap.exists();

  return {
    isAdmin: isGlobalAdmin || isTournamentAdmin,
    isGlobalAdmin,
    isTournamentAdmin,
  };
}

/** Subscribe to auth state changes */
export function onAuthChange(
  callback: (user: User | null) => void,
): () => void {
  return onAuthStateChanged(auth, callback);
}
